import { Zap, ShieldCheck, Wrench, ThumbsUp, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/SectionHeading";

const HERO = "https://customer-assets.emergentagent.com/job_e8a51ae8-0e5c-48cb-98d6-63c31c891959/artifacts/jb8jsxjn_Marketing%20-%20Coffrets%20comptage.png";

const features = [
  { icon: Zap, title: "Raccordement EDF facilité", desc: "Coffrets conformes aux exigences EDF, prêts à l'emploi pour vos chantiers." },
  { icon: ShieldCheck, title: "Matériel sécurisé", desc: "Conforme aux normes en vigueur, contrôlé et entretenu régulièrement." },
  { icon: Wrench, title: "Prêts à l'emploi", desc: "Installation rapide et simple, pose réalisée par nos soins selon les normes." },
  { icon: ThumbsUp, title: "Idéal pour tous chantiers", desc: "BTP, événements, installations provisoires jusqu'à 36 kVA." },
];

const offers = [
  { title: "Coffret monophasé 15/45A", power: "230 V", desc: "Compteur EDF monophasé, idéal pour chantiers résidentiels et petites installations." },
  { title: "Coffret triphasé 30/60A", power: "400 V — 36 kVA", desc: "Compteur EDF triphasé pour chantiers BTP exigeants et installations provisoires de plus grande puissance." },
];

export default function Electrotechnique() {
  return (
    <div data-testid="page-electrotechnique">
      <section className="relative bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#1B3A6B]/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#FBBF24] mb-4">Pôle Électrotechnique</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight max-w-3xl">
            Besoin d'électricité sur votre chantier ?
            <span className="block text-[#FBBF24]">Nous avons la solution</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Location de coffrets de comptage provisoires pour le raccordement EDF et la distribution électrique de vos chantiers, jusqu'à 36 kVA.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHeading eyebrow="Nos garanties" title="Coffrets de comptage provisoires" subtitle="Pour le raccordement EDF et la distribution électrique de votre chantier" />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#1B3A6B]/5 text-[#1B3A6B] mb-4">
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {offers.map((o) => (
            <div key={o.title} className="rounded-2xl bg-gradient-to-br from-[#1B3A6B] to-[#142c54] text-white p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#FBBF24] text-[#1B3A6B] text-xs font-bold">{o.power}</div>
              <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">{o.title}</h3>
              <p className="mt-3 text-slate-300 leading-relaxed">{o.desc}</p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#FBBF24] hover:gap-3 transition-all">
                Demander un devis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-xl bg-amber-50 border border-amber-200 p-5 text-sm text-amber-900">
          <strong>Bon à savoir :</strong> nous réalisons la pose du coffret sur site. Le dossier EDF et les rendez-vous avec EDF sont gérés par le client.
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Un chantier à alimenter, une installation provisoire ou définitive à réaliser, une mise en conformité ?
          </h2>
          <p className="mt-4 text-slate-600">Contactez-nous pour évaluer ensemble vos besoins en puissance et obtenir un devis adapté.</p>
          <Link to="/contact" className="inline-block mt-8">
            <Button size="lg" className="bg-[#1B3A6B] hover:bg-[#142c54] rounded-full px-8 h-12" data-testid="electro-cta">Demander un devis</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
